import { useEffect, useState } from 'react'
import { IconButton } from './ui/primitives'

type Theme = 'system' | 'light' | 'dark'

const KEY = 'quotehero.theme'

function read(): Theme {
  try {
    const stored = localStorage.getItem(KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    return 'system'
  }
}

/**
 * Three states, not two. "System" stamps no attribute at all and lets
 * prefers-color-scheme decide, which is what most people actually want; an
 * explicit choice stamps data-theme and wins in both directions.
 */
export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(read)

  useEffect(() => {
    const root = document.documentElement

    if (theme === 'system') root.removeAttribute('data-theme')
    else root.setAttribute('data-theme', theme)

    try {
      if (theme === 'system') localStorage.removeItem(KEY)
      else localStorage.setItem(KEY, theme)
    } catch {
      /* the choice still holds for this session */
    }
  }, [theme])

  const next: Record<Theme, Theme> = { system: 'light', light: 'dark', dark: 'system' }
  const label: Record<Theme, string> = { system: 'System theme', light: 'Light theme', dark: 'Dark theme' }

  return (
    <IconButton
      title={label[theme]}
      label={`${label[theme]}. Switch to ${label[next[theme]].toLowerCase()}`}
      onClick={() => setTheme(next[theme])}
    >
      <ThemeMark theme={theme} />
    </IconButton>
  )
}

/**
 * One circle, three fills: half for system, empty for light, solid for dark.
 *
 * The same shape throughout rather than three unrelated pictograms, because the control
 * is one thing in three states and a sun that becomes a moon that becomes a monitor reads
 * as three different buttons in the same place. The ring is always drawn, so the light
 * state is a circle rather than an empty patch of header.
 */
function ThemeMark({ theme }: { theme: Theme }) {
  return (
    <svg aria-hidden viewBox="0 0 16 16" className="size-full">
      {theme === 'dark' && <circle cx="8" cy="8" r="6" fill="currentColor" />}

      {/* Clipped to the circle rather than drawn as a semicircle path, so the straight
          edge sits exactly on the diameter at any rendered size. */}
      {theme === 'system' && (
        <>
          <defs>
            <clipPath id="qh-theme-half">
              <circle cx="8" cy="8" r="6" />
            </clipPath>
          </defs>
          <rect x="8" y="1" width="7" height="14" fill="currentColor" clipPath="url(#qh-theme-half)" />
        </>
      )}

      <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  )
}
